import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, FileDown } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import type { SalaryHistory } from "@/types/hr";
import { exportSalaryToPDF } from "@/lib/pdfExport";

interface EmployeeSalaryTableProps {
  employeeId: string;
  employeeName?: string;
  readOnly?: boolean;
  canViewData?: boolean;
  monthlyAverageHours?: number;
}

export function EmployeeSalaryTable({ employeeId, employeeName = "Mitarbeiter", readOnly = false, canViewData: canViewDataProp, monthlyAverageHours: monthlyAverageHoursProp }: EmployeeSalaryTableProps) {
  const { user, profile, userRole } = useAuth();
  
  // Berechne canViewData intern wenn nicht explizit gesetzt
  const isAdmin = userRole === 'admin';
  const isOwnProfile = profile?.employee_id === employeeId;
  const canViewData = canViewDataProp !== undefined ? canViewDataProp : (isAdmin || isOwnProfile);
  const [salaryHistory, setSalaryHistory] = useState<SalaryHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<SalaryHistory>>({
    currency: "EUR",
  });
  // Lade monthlyAverageHours direkt für zuverlässige Berechnung
  const [monthlyAverageHours, setMonthlyAverageHours] = useState<number | undefined>(monthlyAverageHoursProp);

  // Lade Mitarbeiter-Monatsstunden direkt für korrekte Stundenlohn-Berechnung
  useEffect(() => {
    const loadEmployeeHours = async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("monthly_average_hours")
        .eq("id", employeeId)
        .single();

      if (!error && data?.monthly_average_hours) {
        setMonthlyAverageHours(data.monthly_average_hours);
      }
    };
    loadEmployeeHours();
  }, [employeeId]);

  useEffect(() => {
    if (user && employeeId) {
      loadSalaryHistory();
    }
  }, [user, employeeId]);

  const loadSalaryHistory = async () => {
    try {
      const { data, error } = await supabase
        .from("salary_history")
        .select("*")
        .eq("employee_id", employeeId)
        .order("effective_date", { ascending: false });

      if (error) throw error;
      setSalaryHistory(data || []);
    } catch (error) {
      console.error("Error loading salary history:", error);
      toast.error("Fehler beim Laden der Gehaltshistorie");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      currency: "EUR",
    });
    setDialogOpen(true);
  };

  const handleEdit = (entry: SalaryHistory) => {
    setEditingId(entry.id);
    setFormData(entry);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.effective_date || !formData.base_salary) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("salary_history")
          .update({
            effective_date: formData.effective_date,
            base_salary: formData.base_salary,
            currency: formData.currency || "EUR",
            notes: formData.notes,
          })
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Gehaltseintrag erfolgreich aktualisiert");
      } else {
        const { error } = await supabase
          .from("salary_history")
          .insert([{
            employee_id: employeeId,
            effective_date: formData.effective_date!,
            base_salary: formData.base_salary!,
            currency: formData.currency || "EUR",
            salary_type: "monthly",
            notes: formData.notes,
            created_by: user!.id,
          }]);

        if (error) throw error;
        toast.success("Gehaltseintrag erfolgreich gespeichert");
      }

      setDialogOpen(false);
      loadSalaryHistory();
    } catch (error) {
      console.error("Error saving salary history:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Möchten Sie diesen Eintrag wirklich löschen?")) return;

    try {
      const { error } = await supabase
        .from("salary_history")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Eintrag gelöscht");
      loadSalaryHistory();
    } catch (error) {
      console.error("Error deleting salary history:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Gehaltshistorie</CardTitle>
              <CardDescription>Verwalten Sie die Gehaltshistorie</CardDescription>
            </div>
            <div className="flex gap-2">
              {canViewData && (
                <Button variant="outline" onClick={() => exportSalaryToPDF(employeeName, salaryHistory)}>
                  <FileDown className="mr-2 h-4 w-4" />
                  PDF
                </Button>
              )}
              {!readOnly && (
                <Button onClick={handleAdd}>
                  <Plus className="mr-2 h-4 w-4" />
                  Neuer Eintrag
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {salaryHistory.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[100px]">Datum</TableHead>
                  <TableHead className="min-w-[120px]">Bruttolohn</TableHead>
                  <TableHead className="min-w-[100px]">Stundenlohn</TableHead>
                  <TableHead className="min-w-[100px]">Anstieg</TableHead>
                  <TableHead className="min-w-[200px]">Begründung</TableHead>
                  {!readOnly && <TableHead className="min-w-[80px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {salaryHistory.map((entry, index) => {
                  // Verwende mitarbeiterspezifische Stunden oder Standard 173.3
                  const MONTHLY_HOURS = monthlyAverageHours || 173.3;
                  const hourlyWage = entry.base_salary / MONTHLY_HOURS;
                  
                  // Berechne prozentualen Anstieg zur vorherigen Lohnerhöhung
                  // salaryHistory ist nach Datum absteigend sortiert, also ist index+1 der vorherige Eintrag
                  const previousEntry = salaryHistory[index + 1];
                  let percentageIncrease: number | null = null;
                  if (previousEntry) {
                    percentageIncrease = ((entry.base_salary - previousEntry.base_salary) / previousEntry.base_salary) * 100;
                  }
                  
                  return (
                    <TableRow key={entry.id}>
                      <TableCell>
                        {format(new Date(entry.effective_date), "dd.MM.yyyy", { locale: de })}
                      </TableCell>
                      <TableCell>
                        {canViewData ? (
                          entry.base_salary.toLocaleString("de-DE", {
                            style: "currency",
                            currency: entry.currency,
                          })
                        ) : (
                          <span className="text-muted-foreground italic">Vertraulich</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {canViewData ? (
                          hourlyWage.toLocaleString("de-DE", {
                            style: "currency",
                            currency: entry.currency,
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        ) : (
                          <span className="text-muted-foreground italic">Vertraulich</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {canViewData ? (
                          percentageIncrease !== null ? (
                            <span className={percentageIncrease >= 0 ? "text-green-600" : "text-red-600"}>
                              {percentageIncrease >= 0 ? "+" : ""}
                              {percentageIncrease.toLocaleString("de-DE", {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                              })}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Ersteintrag</span>
                          )
                        ) : (
                          <span className="text-muted-foreground italic">Vertraulich</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{entry.notes || "-"}</TableCell>
                      {!readOnly && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(entry)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(entry.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Noch keine Gehaltseinträge vorhanden
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Gehaltseintrag bearbeiten" : "Gehaltseintrag hinzufügen"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gültig ab *</Label>
                <Input
                  type="date"
                  value={formData.effective_date || ""}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Grundgehalt (brutto) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.base_salary || ""}
                  onChange={(e) => setFormData({ ...formData, base_salary: parseFloat(e.target.value) })}
                  placeholder="z.B. 3500"
                />
              </div>
            </div>

            <div>
              <Label>Begründung</Label>
              <Textarea
                rows={3}
                value={formData.notes || ""}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="z.B. Gehaltserhöhung aufgrund guter Leistung, Anpassung nach Probezeit, etc."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
